import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './FilterWrapper.module.scss';
import appStyles from '../../../app/App.module.scss';
import colors from '../../../styles/_colors.module.scss';
import { ReactComponent as Calendar } from '../../../assets/calendar-filter.svg';
import { ReactComponent as Today } from '../../../assets/today.svg';
import { ReactComponent as Tomorrow } from '../../../assets/tomorrow.svg';
import { ReactComponent as TimeOfDay } from '../../../assets/time-of-day.svg';
import { ReactComponent as BudgetOptions } from '../../../assets/budget-options.svg';
import { ReactComponent as NextTrip } from '../../../assets/next-trip.svg';
import { useTranslation } from 'react-i18next';
import {
  SortOptions,
  TagsList,
  TagsListSkeleton,
  TimesOfDayList,
} from '../../filter';
import {
  Carousel,
  DEFAULT_TIMEZONE,
  Datepicker,
  FilterButton,
  Pad,
  useFade,
} from '../../ui';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';
import { TOD_URL_NAME, SORT_URL_NAME, DAY_URL_NAME } from '../';
import classNames from 'classnames';
import { useGetTagsQuery } from '../../../features/api';

interface FilterWrapperProps {
  className?: string;
}

export default function FilterWrapper({
  className,
}: Readonly<FilterWrapperProps>): JSX.Element {
  const { t } = useTranslation();

  const {
    isLoading: isTagsLoading,
    isSuccess: isTagsSuccess,
    isError: isTagsError,
    error,
  } = useGetTagsQuery();

  const navbarRef = useRef<HTMLDivElement | null>(null);
  const filterWrapperRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(true);
  const [isFixed, setIsFixed] = useState<boolean>(false);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(
    window.innerWidth - document.documentElement.clientWidth
  );
  const [navbarHeight, setNavbarHeight] = useState<number>(0);

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const target = entries[0];
    if (target.intersectionRatio === 0) {
      setIsFixed(true);
    } else if (target.intersectionRatio === 1) {
      setIsFixed(false);
    }
  }, []);

  const observerRef = useRef(
    new IntersectionObserver(handleObserver, {
      threshold: [0, 1],
    })
  );

  useEffect(() => {
    navbarRef.current = document.querySelector('.dke_navbar');

    if (filterWrapperRef.current) {
      observerRef.current.observe(filterWrapperRef.current);
    }

    return () => {
      if (filterWrapperRef.current) {
        observerRef.current.unobserve(filterWrapperRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(([e]) => {
      if (navbarRef.current) {
        const navbarMenu = navbarRef.current.querySelector('.dke_navbar__menu');
        navbarMenu?.classList.add('js_dke_hidden');
        setNavbarHeight(navbarRef.current.clientHeight);
        navbarMenu?.classList.remove('js_dke_hidden');
      }

      setScrollbarWidth(window.innerWidth - e.target.clientWidth);
      changeTimesOfDayPopoverAlignment();
    });

    resizeObserver.observe(document.documentElement);

    return () => {
      resizeObserver.unobserve(document.documentElement);
    };
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTimesOfDay = searchParams.get(TOD_URL_NAME)?.split(',') ?? [];
  const selectedSortOption = searchParams.get(SORT_URL_NAME);

  const selectedDateStr = searchParams.get(DAY_URL_NAME);
  const selectedLuxonDate = selectedDateStr
    ? DateTime.fromFormat(selectedDateStr, 'dd.MM.yyyy')
    : null;

  useEffect(() => {
    if (!filterWrapperRef.current) {
      return;
    }

    const filterWrapperBottom =
      filterWrapperRef.current.getBoundingClientRect().bottom;
    const currentWindowScroll = window.scrollY;

    const handleScroll = () => {
      if (!filterWrapperRef.current) {
        return;
      }

      if (
        window.scrollY ===
        filterWrapperBottom + currentWindowScroll - navbarHeight
      ) {
        observerRef.current.unobserve(filterWrapperRef.current);
        setIsFixed(false);
        window.removeEventListener('scroll', handleScroll);

        window.addEventListener(
          'scroll',
          () => {
            if (filterWrapperRef.current) {
              observerRef.current.observe(filterWrapperRef.current);
            }
          },
          { once: true }
        );
      }
    };

    if (filterWrapperBottom < 0) {
      window.addEventListener('scroll', handleScroll);

      window.scroll({
        top: filterWrapperBottom + currentWindowScroll - navbarHeight,
        behavior: 'smooth',
      });
    }
  }, [searchParams]);

  const isTodayActive =
    !!selectedLuxonDate &&
    +DateTime.now().setZone(DEFAULT_TIMEZONE).startOf('day') ===
      +selectedLuxonDate;
  const isTomorrowActive =
    !!selectedLuxonDate &&
    +DateTime.now()
      .setZone(DEFAULT_TIMEZONE)
      .plus({ day: 1 })
      .startOf('day') === +selectedLuxonDate;

  const [isTodayDisabled, setIsTodayDisabled] = useState<boolean>(false);

  useEffect(() => {
    const now = DateTime.now().setZone(DEFAULT_TIMEZONE);
    const currentHour = now.get('hour');

    if (
      currentHour >= 12 &&
      currentHour < 17 &&
      selectedTimesOfDay.length === 1 &&
      selectedTimesOfDay.includes('morning')
    ) {
      setIsTodayDisabled(true);
    } else if (
      currentHour >= 17 &&
      currentHour < 23 &&
      selectedTimesOfDay.length <= 2 &&
      (selectedTimesOfDay.includes('morning') ||
        selectedTimesOfDay.includes('day'))
    ) {
      setIsTodayDisabled(true);
    } else if (
      currentHour >= 23 &&
      currentHour < 6 &&
      selectedTimesOfDay.length <= 3 &&
      (selectedTimesOfDay.includes('morning') ||
        selectedTimesOfDay.includes('day') ||
        selectedTimesOfDay.includes('evening'))
    ) {
      setIsTodayDisabled(true);
    } else {
      setIsTodayDisabled(false);
    }
  }, [selectedTimesOfDay]);

  const timesOfDayPopoverWrapperRef = useRef<HTMLDivElement>(null);
  const timesOfDayPopoverRef = useRef<HTMLDivElement>(null);

  const [
    isTimesOfDayVisible,
    showTimesOfDay,
    setShowTimesOfDay,
    timesOfDayFadeProps,
  ] = useFade(false);

  const [
    isDatepickerVisible,
    showDatepicker,
    setShowDatepicker,
    DatepickerFadeProps,
  ] = useFade(false);

  const togglePopover = useCallback(
    (
      e: React.MouseEvent,
      setShowComponent: (value: SetStateAction<boolean>) => void,
      isComponentVisible: boolean
    ) => {
      e.stopPropagation();
      setShowComponent(!isComponentVisible);
      document.addEventListener(
        'click',
        () => {
          setShowComponent(false);
        },
        { once: true }
      );
    },
    []
  );

  /*TODO: как-то завернуть функционал выравнивания в компонент.
  Скорее всего, отдельный компонент, включающий в себя управляющий элемент для поповера и сам поповер.
  */
  const changeTimesOfDayPopoverAlignment = () => {
    if (!timesOfDayPopoverRef.current || !timesOfDayPopoverWrapperRef.current) {
      return;
    }

    const timesOfDayPopoverWidth = timesOfDayPopoverRef.current.offsetWidth;
    const distanceToRightEdge =
      document.documentElement.clientWidth -
      timesOfDayPopoverWrapperRef.current.getBoundingClientRect().left;

    if (distanceToRightEdge < timesOfDayPopoverWidth + 10) {
      timesOfDayPopoverRef.current.style.right = '0';
      timesOfDayPopoverRef.current.style.left = 'auto';
    } else {
      timesOfDayPopoverRef.current.style.right = '';
      timesOfDayPopoverRef.current.style.left = '';
    }
  };

  useEffect(() => {
    changeTimesOfDayPopoverAlignment();
  }, [isTimesOfDayVisible]);

  const todayAndTomorrowClickHandler = (
    isDayActive: boolean,
    day: DateTime
  ) => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (isDayActive) {
      newSearchParams.delete(DAY_URL_NAME);
    } else {
      newSearchParams.set(DAY_URL_NAME, day.toFormat('dd.MM.yyyy'));
    }

    setSearchParams(newSearchParams);
  };

  const sortOptionClickHandler = (sortOption: SortOptions) => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (selectedSortOption === sortOption) {
      newSearchParams.delete(SORT_URL_NAME);
    } else {
      newSearchParams.set(SORT_URL_NAME, sortOption);
    }

    setSearchParams(newSearchParams);
  };

  let carouselContent = <></>;

  if (isTagsLoading) {
    carouselContent = <TagsListSkeleton />;
  }

  if (isTagsSuccess) {
    carouselContent = <TagsList isJustified={!isScrollable} />;
  }

  if (isTagsError) {
    carouselContent = <div>{error.toString()}</div>;
  }

  return (
    <div
      ref={filterWrapperRef}
      className={classNames(styles.root, className && className)}
    >
      <div
        className={classNames(
          appStyles.container,
          styles.container,
          isFixed && styles.fixed,
          'dke_filter__container'
        )}
        style={{ top: isFixed ? navbarHeight : undefined }}
      >
        <Pad
          style={
            { '--scrollbarWidth': scrollbarWidth + 'px' } as React.CSSProperties
          }
          className={styles.pad}
        >
          <Carousel
            className={styles.tagsList}
            setIsScrollable={setIsScrollable}
          >
            {carouselContent}
          </Carousel>

          <div className={styles.filters}>
            {/* Календарь */}
            <div className={classNames(styles.popoverWrapper, styles.calendar)}>
              <FilterButton
                Icon={Calendar}
                iconStroke={colors.primary}
                active={showDatepicker || !!selectedLuxonDate}
                className={classNames(
                  styles.filterButton,
                  styles.popoverButton,
                  'FilterButton'
                )}
                onClick={e => {
                  if (isTimesOfDayVisible) {
                    togglePopover(e, setShowTimesOfDay, isTimesOfDayVisible);
                  }
                  togglePopover(e, setShowDatepicker, isDatepickerVisible);
                }}
              >
                {selectedLuxonDate
                  ? selectedLuxonDate.toFormat('dd.LL.yy')
                  : t('date')}
              </FilterButton>
              {isDatepickerVisible && (
                <div
                  className={styles.popover}
                  {...DatepickerFadeProps}
                  onClick={e => e.stopPropagation()}
                >
                  <Pad className={styles.datepicker}>
                    <Datepicker
                      selectedDate={selectedLuxonDate}
                      setSelectedDate={luxonDate => {
                        setShowDatepicker(false);
                        const newSearchParams = new URLSearchParams(
                          searchParams
                        );
                        newSearchParams.set(
                          DAY_URL_NAME,
                          luxonDate.toFormat('dd.MM.yyyy')
                        );
                        setSearchParams(newSearchParams);
                      }}
                      clearDate={() => {
                        setShowDatepicker(false);

                        const newSearchParams = new URLSearchParams(
                          searchParams
                        );
                        newSearchParams.delete(DAY_URL_NAME);
                        setSearchParams(newSearchParams);
                      }}
                      timezone={DEFAULT_TIMEZONE}
                      locale={document.documentElement.lang}
                    />
                  </Pad>
                </div>
              )}
            </div>
            {/* Кнопка «Сегодня» */}
            <FilterButton
              Icon={Today}
              iconStroke={colors.primary}
              active={isTodayActive}
              disabled={isTodayDisabled}
              className={classNames(
                styles.filterButton,
                styles.today,
                'FilterButton'
              )}
              onClick={() =>
                todayAndTomorrowClickHandler(
                  isTodayActive,
                  DateTime.now().setZone(DEFAULT_TIMEZONE).startOf('day')
                )
              }
            >
              {t('today')}
            </FilterButton>
            {/* Кнопка «Завтра» */}
            <FilterButton
              Icon={Tomorrow}
              iconStroke={colors.primary}
              active={isTomorrowActive}
              className={classNames(
                styles.filterButton,
                styles.tomorrow,
                'FilterButton'
              )}
              onClick={() =>
                todayAndTomorrowClickHandler(
                  isTomorrowActive,
                  DateTime.now()
                    .setZone(DEFAULT_TIMEZONE)
                    .plus({ day: 1 })
                    .startOf('day')
                )
              }
            >
              {t('tomorrow')}
            </FilterButton>
            {/* Время суток */}
            <div
              className={classNames(
                styles.popoverWrapper,
                styles.timesOfDayWrapper
              )}
              ref={timesOfDayPopoverWrapperRef}
            >
              <FilterButton
                Icon={TimeOfDay}
                iconStroke={colors.primary}
                active={showTimesOfDay || !!selectedTimesOfDay.length}
                className={classNames(
                  styles.filterButton,
                  styles.popoverButton,
                  'FilterButton'
                )}
                onClick={e => {
                  if (isDatepickerVisible) {
                    togglePopover(e, setShowDatepicker, isDatepickerVisible);
                  }
                  togglePopover(e, setShowTimesOfDay, isTimesOfDayVisible);
                }}
              >
                {t('time')}
              </FilterButton>
              {isTimesOfDayVisible && (
                <div
                  className={styles.popover}
                  ref={timesOfDayPopoverRef}
                  {...timesOfDayFadeProps}
                  onClick={e => e.stopPropagation()}
                >
                  <Pad className={styles.timesOfDay}>
                    <TimesOfDayList />
                  </Pad>
                </div>
              )}
            </div>
            {/* Сортировка по цене */}
            <FilterButton
              Icon={BudgetOptions}
              iconStroke={colors.orange}
              theme='orange'
              active={selectedSortOption === 'price'}
              className={classNames(
                styles.filterButton,
                styles.price,
                'SortButton'
              )}
              onClick={() => sortOptionClickHandler('price')}
            >
              {t('budget')}
            </FilterButton>
            {/* Сортировка по ближайшему рейсу */}
            <FilterButton
              Icon={NextTrip}
              iconStroke={colors.orange}
              theme='orange'
              active={selectedSortOption === 'next_trip'}
              className={classNames(
                styles.filterButton,
                styles.nextTrip,
                'SortButton'
              )}
              onClick={() => sortOptionClickHandler('next_trip')}
            >
              {t('next')}
            </FilterButton>
          </div>
        </Pad>
      </div>
    </div>
  );
}
